import { useQuery } from '@tanstack/react-query';
import { Button } from '@thedealersconcierge/components';
import parsePhoneNumber from 'libphonenumber-js';
import { useTranslation } from 'react-i18next';
import logoutAction from '~/actions/logoutAction';
import Spinner from '~/components/Spinner';
import meQuery from '~/queries/meQuery';
import transactionQuery from '~/queries/transactionQuery';
import { useNavigate, useParams } from '~/router';
import PersonalInformationSection from './_components/PersonalInformationSection';
import TransactionDataSection from './_components/TransactionDataSection';

const DashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { transactionId, dealershipSlug } = useParams(
    '/dashboard/:dealershipSlug/:transactionId'
  );
  const { data: transactionQueryData, isLoading } = useQuery(
    transactionQuery(transactionId, dealershipSlug)
  );
  const { data } = useQuery(meQuery());
  const profileData = data?.me?.user;
  const transaction = transactionQueryData?.transaction;
  const isCoBuyer =
    !!data?.me?.user?.id && transaction?.coBuyerId === data.me.user.id;
  const vehicle = transactionQueryData?.transaction?.vehicle;
  const tradeVehicle = transactionQueryData?.transaction?.tradeVehicle;
  const handleGoToPersonalInformation = () => {
    navigate('/dashboard/:dealershipSlug/:transactionId/personalInformation', {
      params: { transactionId, dealershipSlug }
    });
  };
  const handleGoToPurchaseVehicle = () => {
    navigate('/dashboard/:dealershipSlug/:transactionId/purchase-vehicle', {
      params: {
        transactionId,
        dealershipSlug
      }
    });
  };

  return (
    <div data-test-id={'consumer-dashboard-body'}>
      {/**
       * We might want to think about a mobile menu.
       */}
      <div className="flex flex-col items-center bg-secondary px-spacing-05">
        <div className="flex flex-row items-center justify-end w-full max-w-screen-lg pt-spacing-04">
          {/**
           * This should technically be a link but for visual consistency we keep it as a button for now.
           */}
          <Button
            label={t('Settings')}
            variant="GHOST"
            size="SMALL"
            onClick={() => {
              navigate('/user/security');
            }}
          />

          <div className="flex h-4 border-r border-primary" />

          <Button
            label={t('Logout')}
            variant="GHOST"
            size="SMALL"
            onClick={logoutAction}
          />
        </div>
      </div>

      {transaction && (
        <PersonalInformationSection
          transactionRole={isCoBuyer ? 'CO_BUYER' : 'BUYER'}
          transaction={transaction}
          isBuyer={!isCoBuyer}
          name={
            (!isCoBuyer
              ? transactionQueryData.transaction?.customerSharedData?.buyerName
              : transactionQueryData.transaction?.customerSharedData
                  ?.coBuyerName) ?? '-'
          }
          phoneNumber={
            profileData?.phoneNumber
              ? (parsePhoneNumber(profileData.phoneNumber)?.format(
                  parsePhoneNumber(profileData.phoneNumber)?.country === 'US'
                    ? 'NATIONAL'
                    : 'INTERNATIONAL'
                ) ?? '-')
              : '-'
          }
          email={profileData?.email ?? '-'}
          onEditPersonalInformation={handleGoToPersonalInformation}
        />
      )}

      {isLoading || !transaction ? (
        <div className="flex justify-center pt-10">
          <Spinner />
        </div>
      ) : (
        <TransactionDataSection
          transaction={transaction}
          transactionRole={isCoBuyer ? 'CO_BUYER' : 'BUYER'}
          selectedTransactionId={transactionId}
          tradeVehicle={tradeVehicle}
          purchaseVehicle={vehicle}
          onEditPurchaseVehicle={handleGoToPurchaseVehicle}
          onAddPurchaseVehicle={handleGoToPurchaseVehicle}
        />
      )}
    </div>
  );
};

export default DashboardPage;
